import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Import images
import caseStudy1 from '../assets/images/case-study-1.jpg';
import caseStudy2 from '../assets/images/case-study-2.jpg';
import caseStudy3 from '../assets/images/case-study-3.jpg';
import textgradCover from '../assets/Screenshot from 2025-06-11 13-37-05.png';
import salesAgent1 from '../assets/Screenshot from 2025-06-07 23-26-28.png';
import salesAgent2 from '../assets/Screenshot from 2025-06-07 23-51-23.png';
import graphrag1 from '../assets/Pasted image.png';
import graphrag2 from '../assets/Screenshot from 2025-06-09 22-45-42.png';
import graphrag3 from '../assets/Screenshot from 2025-06-10 03-26-41.png';

const CaseStudiesSection = styled.section`
  padding-top: 8rem;
  background-color: #121212;
  text-align: center;
`;

const CaseStudiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const CaseStudyCard = styled.div`
  background-color: #1e1e1e;
  border-radius: 15px;
  padding: 0;
  text-align: left;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(59, 130, 246, 0.2);
    border-color: #3b82f6;
  }

  .image-container {
    height: 200px;
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    @media (max-width: 480px) {
      height: 150px;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .icon {
      font-size: 4rem;
      color: white;
      opacity: 0.8;
    }
  }

  .content {
    padding: 2rem;

    @media (max-width: 480px) {
      padding: 1.25rem;
    }
  }

  .category {
    color: #3b82f6;
    font-size: 0.9rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.5rem;
  }

  h3 {
    color: #e0e0e0;
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    line-height: 1.3;
  }

  p {
    color: #a0a0a0;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }

  .metrics {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }

  .metric {
    .value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #3b82f6;

      @media (max-width: 480px) {
        font-size: 1.2rem;
      }
    }

    .label {
      font-size: 0.8rem;
      color: #a0a0a0;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .technologies {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }

  .tech-tag {
    background-color: #333;
    color: #e0e0e0;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 500;
  }

  .read-more {
    color: #3b82f6;
    font-weight: 500;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: color 0.3s ease;

    &:hover {
      color: #60a5fa;
    }

    &::after {
      content: '→';
    }
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background-color: ${props => props.$active ? '#3b82f6' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#a0a0a0'};
  border: 2px solid #3b82f6;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: #3b82f6;
    color: white;
  }
`;


const CaseStudies = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const navigate = useNavigate();

  const caseStudies = [
    {
      categories: ['Quant Finance', 'E-commerce'],
      title: 'Astra: AI Sales Development Representative',
      description: 'Automated prospecting, enrichment, and outreach for premium brands. Integrated LangChain agents with Pydantic workflows for high-precision lead handling.',
      icon: '🤖',
      image: caseStudy1,
      images: [salesAgent1, salesAgent2],
      metrics: [
        { value: '+35%', label: 'Qualified Meetings' },
        { value: '–60%', label: 'Manual Prospecting' },
        { value: '200+', label: 'Leads/Week' }
      ],
      technologies: ['LangChain Agents', 'Pydantic Workflows', 'React UI', 'Wall Street Targeting'],
      slug: 'astra-sales-chatbot',
      content: `<h2>Custom Tailored Agentic AI Chatbots: Reimagine Online Sales, Boost Conversions, Save Costs and Human Efforts</h2>

<p><strong>How ContextJet AI Built a Personalized Sales Chatbot to Power E-Commerce Experiences</strong></p>

<hr class="section-divider">

<h3>Overview</h3>

<p>In the fast-moving world of e-commerce, engaging customers at the right time, with the right information, is critical. But not every online store has the resources to offer real-time, knowledgeable, 24/7 sales support. That's exactly where <strong>Astra</strong>, our intelligent AI sales assistant, comes in.</p>

<p>This chatbot was originally developed for a client under a different brand. For demonstration and confidentiality purposes, we've used hypothetical names like <strong>Zofer Electronics</strong> (as the e-commerce business) and <strong>Astra</strong> (as the chatbot). The underlying product and catalog data showcased here have been <strong>synthesized using our Synthetic Data Generation Suite</strong> to mimic a realistic retail environment while maintaining complete privacy.</p>

<p>While the names are fictional, <strong>Astra is very real</strong> - a flexible, agentic sales chatbot that combines powerful AI models, real-time data sources, and smart persona logic to deliver a conversational experience that feels both <strong>personal and informed</strong>.</p>`
    },
    {
      categories: ['CPG', 'Market Research'],
      title: 'Multimodal Synthetic Survey Suite',
      description: 'Built a multi-agent framework to synthesize real-time respondent data for text and image studies with ~90% accuracy vs live benchmarks.',
      icon: '📊',
      image: caseStudy3,
      images: [],
      metrics: [
        { value: '$300K+', label: 'Revenue Generated' },
        { value: 'MAPE < 10%', label: 'Error Rate' },
        { value: '10x', label: 'Lower Cost' }
      ],
      technologies: ['Azure Deployment', 'Multi-agent Pipelines', 'Synthetically Generated Data'],
      slug: 'synthetic-survey-suite',
      content: `Revolutionizing Market Research with ContextJet AI's Synthetic Market Survey Suite

From Weeks to Hours: Rethinking Market Insights with AI

At ContextJet AI, we've always believed that innovation shouldn't be slowed down by outdated research processes. For years, companies have relied on traditional surveys - ones that take weeks to design, run, and analyze - to figure out if their product, price, or packaging is on the right track. But let's be honest: that process can be slow, expensive, and inflexible.

That's exactly the problem one of our global consumer goods clients faced. Their team needed quick, reliable feedback on multiple product ideas across different types of customers.

Their question was simple: "Can we get quality market feedback without having to run a full survey each time?"

Our answer? Absolutely. And faster than you think.`
    },
    {
      categories: ['Compliance', 'Legal'],
      title: 'Nexo: Knowledge GraphRAG Platform',
      description: 'Engineered an entity-linked graph over massive document stores for import/export compliance workflows utilizing Neo4j and LangGraph.',
      icon: '🕸️',
      image: caseStudy2,
      images: [graphrag1, graphrag2, graphrag3],
      metrics: [
        { value: '+87%', label: 'Answer Precision' },
        { value: '–45%', label: 'Research Time' },
        { value: 'Salesforce', label: 'Integration' }
      ],
      technologies: ['Neo4j', 'LangGraph Architecture', 'Vector DB', 'Auditable Citations'],
      slug: 'nexo-graphrag-engine',
      content: `<h2>Unlocking Knowledge with Multimodal AI-Powered Graph Reasoning</h2>

<p><strong>How ContextJet AI Converts Scattered Enterprise Data into a Smart, Searchable Graph Knowledge Base</strong></p>

<hr class="section-divider">

<h3>Overview</h3>

<p>In today's enterprises, knowledge is everywhere - but it's often trapped in PDFs, spreadsheets, scattered web portals, image snapshots, and emails. The challenge isn't access; it's structure.</p>

<p>That's where our solution comes in. At <strong>ContextJet AI</strong>, we built a powerful system to <strong>turn unstructured and semi-structured data into intelligent, query-ready knowledge graphs</strong> - complete with a friendly chatbot interface and robust retrieval strategies.</p>

<p>Meet a fictional but visually demonstrated assistant called <strong>Nexo</strong> - our intelligent Graph Reasoning Chatbot. While Nexo is a demo interface, the system behind it has been developed and deployed for clients needing deep search, insight synthesis, and AI-driven document reasoning across multiple data types.</p>`
    },
    {
      categories: ['E-commerce', 'Manufacturing', 'Media and Entertainment'],
      title: 'TextGrad-Based Optimization Suite',
      description: 'This innovative solution is currently under development and will leverage advanced text optimization techniques for enhanced AI performance.',
      icon: '⚡',
      image: textgradCover,
      images: [],
      metrics: [
        { value: 'Coming', label: 'Soon' },
        { value: 'In Progress', label: 'Development' },
        { value: 'Advanced', label: 'Technology' }
      ],
      technologies: ['TextGrad', 'Optimization', 'Advanced AI', 'Coming Soon'],
      slug: 'textgrad-optimization',
      content: `<h2>TextGrad-Based Optimization Suite</h2>

<p>Coming soon! Solution building still in progress.</p>`
    }
  ];

  const categories = ['All', 'E-commerce', 'Manufacturing', 'Media and Entertainment'];

  const filteredStudies = activeFilter === 'All'
    ? caseStudies
    : caseStudies.filter(study => study.categories.includes(activeFilter));

  return (
    <>
      <CaseStudiesSection>
        <div className="container">
          <h2 className="section-subtitle">Case Studies</h2>
          <h1 className="section-title">Success Stories</h1>
          <p className="section-description" style={{ margin: '0 auto' }}>
            Discover how our AI solutions have transformed businesses across industries,
            delivering measurable results and driving innovation.
          </p>

          <FilterSection>
            {categories.map((category, index) => (
              <FilterButton
                key={index}
                $active={activeFilter === category}
                onClick={() => setActiveFilter(category)}
              >
                {category}
              </FilterButton>
            ))}
          </FilterSection>

          <CaseStudiesGrid>
            {filteredStudies.map((study, index) => (
              <CaseStudyCard
                key={index}
                onClick={() => navigate(`/case-studies/${study.slug}`)}
              >
                <div className="image-container">
                  {study.image ? (
                    <img src={study.image} alt={study.title} />
                  ) : (
                    <span className="icon">{study.icon}</span>
                  )}
                </div>
                <div className="content">
                  <div className="category">{study.categories[0]}</div>
                  <h3>{study.title}</h3>
                  <p>{study.description}</p>

                  <div className="read-more">
                    Read Full Case Study
                  </div>
                </div>
              </CaseStudyCard>
            ))}
          </CaseStudiesGrid>
        </div>
      </CaseStudiesSection>

    </>
  );
};

export default CaseStudies;